import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Cascader, DatePicker, Input, NavBar, SpinLoading, Toast } from 'antd-mobile';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../app/providers/AuthProvider';
import { memberApi } from '../../features/member/api/memberApi';
import Icon from '../../icon';
import { transformedData } from '../UploadMember/region';
import MemberAvatarManager from './MemberAvatarManager';
import './index.css';

const EMPTY_FORM = {
    name: '',
    mobile: '',
    birthday: '',
    area: '',
    joinDate: '',
    memberNum: '',
    height: '',
    weight: '',
    status: 0,
};

const normalizeDate = value => {
    if (!value) return '';
    return String(value).slice(0, 10);
};

const normalizeNullableNumber = value => {
    if (value === '' || value === null || value === undefined) return null;
    return Number(value);
};

const parseDateValue = value => {
    if (!value) return null;
    const [year, month, day] = String(value).split('-').map(Number);
    return year && month && day ? new Date(year, month - 1, day) : null;
};

const formatDateValue = date => {
    if (!(date instanceof Date)) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const formatDateLabel = value => value ? value.replace(/-/g, '/') : '请选择';

export default function AdminEditMember({ selfMode = false }) {
    const navigate = useNavigate();
    const { memberId: routeMemberId } = useParams();
    const { memberInfo, signIn } = useAuth();
    const memberId = selfMode ? memberInfo?.id : routeMemberId;
    const [form, setForm] = useState(EMPTY_FORM);
    const [memberImages, setMemberImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [datePickerField, setDatePickerField] = useState('');
    const [areaVisible, setAreaVisible] = useState(false);
    const [areaValue, setAreaValue] = useState([]);

    const isAdmin = Number(memberInfo?.admin) === 1;
    const today = useMemo(() => formatDateValue(new Date()), []);
    const primaryImage = useMemo(
        () => memberImages.find(item => Number(item.type) === 0),
        [memberImages]
    );

    const refreshAvatarData = useCallback(async () => {
        if (!memberId) return;
        try {
            const response = await memberApi.getInfo(memberId);
            if (response.data?.code !== 200 || !response.data?.data) {
                throw new Error(response.data?.msg || '头像资料读取失败');
            }
            setMemberImages(Array.isArray(response.data.data.memberImg) ? response.data.data.memberImg : []);
        } catch (error) {
            Toast.show({
                icon: 'fail',
                content: error?.response?.data?.msg || error?.message || '头像资料刷新失败',
            });
        }
    }, [memberId]);

    useEffect(() => {
        if (!memberInfo) return;
        if (!memberId) return;
        if (!selfMode && !isAdmin) {
            Toast.show({ icon: 'fail', content: '只有管理员可以编辑球员资料' });
            navigate('/member', { replace: true });
            return;
        }

        let cancelled = false;
        setLoading(true);
        memberApi.getInfo(memberId)
            .then(response => {
                if (cancelled) return;
                if (response.data?.code !== 200 || !response.data?.data) {
                    throw new Error(response.data?.msg || '球员资料不存在');
                }
                const data = response.data.data;
                setMemberImages(Array.isArray(data.memberImg) ? data.memberImg : []);
                // 接口返回的数字保留为字符串，避免空值被输入框错误显示为 0。
                setForm({
                    name: data.name || '',
                    mobile: data.mobile || '',
                    birthday: normalizeDate(data.birthday),
                    area: data.area || '',
                    joinDate: normalizeDate(data.joinDate),
                    memberNum: data.memberNum ?? '',
                    height: data.height ?? '',
                    weight: data.weight ?? '',
                    status: Number(data.status) === 1 ? 1 : 0,
                });
            })
            .catch(error => {
                if (cancelled) return;
                Toast.show({
                    icon: 'fail',
                    content: error?.response?.data?.msg || error?.message || '读取球员资料失败',
                });
                navigate('/member', { replace: true });
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [isAdmin, memberId, memberInfo, navigate, selfMode]);

    const updateField = (field, value) => {
        setForm(previous => ({ ...previous, [field]: value }));
    };

    const validateForm = () => {
        if (!form.name.trim()) return '请输入球员姓名';
        if (form.birthday && form.birthday > today) return '生日不能晚于当前日期';
        if (!selfMode && form.joinDate && form.joinDate > today) return '入队日期不能晚于当前日期';

        const memberNum = normalizeNullableNumber(form.memberNum);
        const height = normalizeNullableNumber(form.height);
        const weight = normalizeNullableNumber(form.weight);
        if (!selfMode && memberNum !== null && (!Number.isInteger(memberNum) || memberNum < 0 || memberNum > 999)) {
            return '球衣号码应为 0 到 999 的整数';
        }
        if (height !== null && (Number.isNaN(height) || height < 50 || height > 250)) {
            return '身高应在 50 到 250 厘米之间';
        }
        if (weight !== null && (Number.isNaN(weight) || weight < 20 || weight > 250)) {
            return '体重应在 20 到 250 公斤之间';
        }
        return '';
    };

    const handleSave = async () => {
        if (saving) return;
        const errorMessage = validateForm();
        if (errorMessage) {
            Toast.show({ icon: 'fail', content: errorMessage });
            return;
        }

        setSaving(true);
        try {
            const editableInfo = {
                name: form.name.trim(),
                mobile: form.mobile.trim() || null,
                birthday: form.birthday || null,
                area: form.area.trim() || null,
                height: normalizeNullableNumber(form.height),
                weight: normalizeNullableNumber(form.weight),
            };
            // 本人模式不向接口提交入队日期、球衣号码和账号状态，避免仅靠界面隐藏权限。
            const response = selfMode
                ? await memberApi.updateOwnInfo(editableInfo)
                : await memberApi.adminUpdateInfo({
                    ...editableInfo,
                    memberId,
                    joinDate: form.joinDate || null,
                    memberNum: normalizeNullableNumber(form.memberNum),
                    status: Number(form.status),
                });
            // 即使资料与原值相同、数据库返回未影响行，也视为一次有效保存。
            if (response.data?.code !== 200) {
                throw new Error(response.data?.msg || '保存失败');
            }
            Toast.show({ icon: 'success', content: '球员资料已保存' });
            if (selfMode) {
                // 同步本地登录信息，避免返回个人页后仍短暂显示修改前的姓名等资料。
                signIn({
                    ...memberInfo,
                    name: form.name.trim(),
                    mobile: form.mobile.trim() || null,
                    birthday: form.birthday || null,
                    area: form.area.trim() || null,
                    height: normalizeNullableNumber(form.height),
                    weight: normalizeNullableNumber(form.weight),
                });
            }
            navigate(selfMode ? `/members/${memberId}` : '/member', { replace: true });
        } catch (error) {
            Toast.show({
                icon: 'fail',
                content: error?.response?.data?.msg || error?.message || '保存失败，请稍后重试',
            });
        } finally {
            setSaving(false);
        }
    };

    const renderTextField = ({ label, field, placeholder, type = 'text', maxLength, inputMode }) => (
        <label className="admin-member-field">
            <span className="admin-member-field-label">{label}</span>
            <Input
                value={String(form[field] ?? '')}
                type={type}
                maxLength={maxLength}
                inputMode={inputMode}
                clearable
                placeholder={placeholder}
                onChange={value => updateField(field, value)}
            />
        </label>
    );

    return (
        <div className="admin-member-edit-page has-unified-nav ui-page ui-page--with-nav">
            <NavBar className="unified-navbar fixed" onBack={() => navigate(-1)}>
                {selfMode ? '修改个人资料' : '编辑球员资料'}
            </NavBar>

            {loading ? (
                <div className="admin-member-loading">
                    <SpinLoading color="primary" />
                    <span>正在读取球员资料</span>
                </div>
            ) : (
                <main className="admin-member-edit-content">
                    <section className="admin-member-preview">
                        <div className={`admin-member-preview-avatar ${Number(primaryImage?.status) === 1 ? 'is-pending' : ''}`}>
                            <img src={primaryImage?.image || Icon.KEPENG_LOGO} alt={form.name || '球员头像'} />
                            <span>#{form.memberNum === '' ? '--' : form.memberNum}</span>
                        </div>
                        <div className="admin-member-preview-copy">
                            <span>{selfMode ? '个人资料维护' : '管理员资料维护'}</span>
                            <strong>{form.name || '未填写姓名'}</strong>
                            <small>{selfMode ? '头像提交后需管理员审核' : (form.status === 0 ? '账号正常使用' : '账号当前已禁用')}</small>
                        </div>
                        {!selfMode && (
                            <i className={form.status === 0 ? 'is-enabled' : 'is-disabled'}>
                                {form.status === 0 ? '正常' : '禁用'}
                            </i>
                        )}
                    </section>

                    <MemberAvatarManager
                        memberId={memberId}
                        images={memberImages}
                        onRefresh={refreshAvatarData}
                        canAudit={!selfMode}
                        adminDirect={!selfMode}
                    />

                    <section className="admin-member-form-card">
                        <header className="admin-member-section-title">
                            <strong>基础身份</strong>
                            <span>维护球员用于展示与联系的个人信息</span>
                        </header>
                        <div className="admin-member-fields">
                            {renderTextField({ label: '姓名', field: 'name', placeholder: '请输入真实姓名', maxLength: 50 })}
                            {renderTextField({ label: '电话号码', field: 'mobile', placeholder: '选填，仅管理员和本人可见', maxLength: 30, inputMode: 'tel' })}
                            <button
                                type="button"
                                className="admin-member-field admin-member-picker-field"
                                onClick={() => setDatePickerField('birthday')}
                            >
                                <span className="admin-member-field-label">生日</span>
                                <span className={`admin-member-picker-value ${form.birthday ? '' : 'is-placeholder'}`}>
                                    {formatDateLabel(form.birthday)}<i>›</i>
                                </span>
                            </button>
                            <button
                                type="button"
                                className="admin-member-field admin-member-picker-field"
                                onClick={() => setAreaVisible(true)}
                            >
                                <span className="admin-member-field-label">籍贯</span>
                                <span className={`admin-member-picker-value ${form.area ? '' : 'is-placeholder'}`}>
                                    {form.area || '请选择省、市、区'}<i>›</i>
                                </span>
                            </button>
                        </div>
                    </section>

                    <section className="admin-member-form-card">
                        <header className="admin-member-section-title">
                            <strong>球员档案</strong>
                            <span>姓名首字母与年龄会根据姓名、生日自动计算</span>
                        </header>
                        <div className="admin-member-fields">
                            {selfMode ? (
                                <div className="admin-member-field admin-member-readonly-field">
                                    <span className="admin-member-field-label">入队日期</span>
                                    <span className={`admin-member-picker-value ${form.joinDate ? '' : 'is-placeholder'}`}>
                                        {formatDateLabel(form.joinDate)}<i>只读</i>
                                    </span>
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    className="admin-member-field admin-member-picker-field"
                                    onClick={() => setDatePickerField('joinDate')}
                                >
                                    <span className="admin-member-field-label">入队日期</span>
                                    <span className={`admin-member-picker-value ${form.joinDate ? '' : 'is-placeholder'}`}>
                                        {formatDateLabel(form.joinDate)}<i>›</i>
                                    </span>
                                </button>
                            )}
                            <div className="admin-member-number-grid">
                                {selfMode ? (
                                    <div className="admin-member-field admin-member-readonly-field">
                                        <span className="admin-member-field-label">球衣号码</span>
                                        <span className={`admin-member-picker-value ${form.memberNum === '' ? 'is-placeholder' : ''}`}>
                                            {form.memberNum === '' ? '未设置' : form.memberNum}<i>只读</i>
                                        </span>
                                    </div>
                                ) : renderTextField({ label: '球衣号码', field: 'memberNum', placeholder: '选填', type: 'number', inputMode: 'numeric' })}
                                {renderTextField({ label: '身高（cm）', field: 'height', placeholder: '选填', type: 'number', inputMode: 'decimal' })}
                                {renderTextField({ label: '体重（kg）', field: 'weight', placeholder: '选填', type: 'number', inputMode: 'decimal' })}
                            </div>
                        </div>
                    </section>

                    {!selfMode && <section className="admin-member-form-card">
                        <header className="admin-member-section-title">
                            <strong>账号状态</strong>
                            <span>禁用后该球员将无法正常登录，请谨慎操作</span>
                        </header>
                        <div className="admin-member-status-options">
                            <button
                                type="button"
                                className={form.status === 0 ? 'is-selected is-enabled' : ''}
                                onClick={() => updateField('status', 0)}
                            >
                                <span>✓</span>
                                <div><strong>正常使用</strong><small>允许该球员登录系统</small></div>
                            </button>
                            <button
                                type="button"
                                className={form.status === 1 ? 'is-selected is-disabled' : ''}
                                onClick={() => updateField('status', 1)}
                            >
                                <span>!</span>
                                <div><strong>禁用账号</strong><small>暂时禁止该球员登录</small></div>
                            </button>
                        </div>
                    </section>}

                    <Button
                        block
                        color="primary"
                        size="large"
                        loading={saving}
                        className="admin-member-save-button"
                        onClick={handleSave}
                    >
                        保存球员资料
                    </Button>
                </main>
            )}

            <DatePicker
                visible={Boolean(datePickerField)}
                title={datePickerField === 'birthday' ? '选择生日' : '选择入队日期'}
                precision="day"
                value={parseDateValue(datePickerField ? form[datePickerField] : '')
                    || (datePickerField === 'birthday' ? new Date(1990, 0, 1) : new Date())}
                min={datePickerField === 'birthday' ? new Date(1930, 0, 1) : new Date(2000, 0, 1)}
                max={parseDateValue(today)}
                onClose={() => setDatePickerField('')}
                onConfirm={date => {
                    if (datePickerField) updateField(datePickerField, formatDateValue(date));
                    setDatePickerField('');
                }}
            />

            <Cascader
                options={transformedData}
                visible={areaVisible}
                value={areaValue}
                onClose={() => setAreaVisible(false)}
                onConfirm={(value, extend = {}) => {
                    setAreaValue(value);
                    // 与 ChangeUser 保持一致，保存省、市、区的中文名称组合。
                    const areaText = extend.items?.map(item => item?.label).filter(Boolean).join('') || value.join('');
                    updateField('area', areaText);
                    setAreaVisible(false);
                }}
            />
        </div>
    );
}
